import React, { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";

import { Button, Modal, Dropdown, Card, Row, Col, Select, Input, Table, Segmented, ConfigProvider, Spin } from "antd";
import ScrollDatePicker from "../../components/calendar/ScrollDatePicker";
import esES from "antd/locale/es_ES";
import { HappyProvider } from "@ant-design/happy-work-theme";
import { SearchOutlined } from "@ant-design/icons";
import { DataBadge, AppPagination } from "../../components/ui";
import { usePermisos } from "../../context/PermisosContext";

import "../../components/tribunal/TribunalPage.css";
import "../../components/tribunal/TribunalTablePage.css";
import "./materias/laboral/LaboralProcedimientoPage.css";

// tus acciones reales (mismas que usas en LaboralProcedimientoPage)
import { actionAutoridadesTribunalGet } from "../../redux/actions/autoridades/autoridades.js";
import { actionTribunalCards } from "../../redux/actions/tribunal/tribunal.js";
import { printTribunalReportPdf, previewTribunalReportPdf } from "../../components/utils/printTribunalReportPdf.js";

// tus hooks reales (mismos que ya tienes)
import useLaboralCatalogos from "./materias/laboral/useLaboralCatalogos.js";
import useTribunalProcedimiento from "./materias/laboral/useTribunalProcedimiento.js";
import useDefaultCiudadUsuario from "./materias/laboral/useDefaultCiudadUsuario.js";

// tu config real (si ya existe ahí)
import { laboralTiposConfig, PAGE_SIZE } from "./materias/laboral/laboralProcedimiento.config.js";
import ImportarExcelModal from "./utils/ImportarExcelModal.jsx";
import { computeTribunalEstatus, STATUS_PILL_HEX } from "./materias/laboral/tribunalEstatus.js";

dayjs.locale("es");

const ALL = "__all__";

const coerceItems = (slice) => {
  if (!slice) return [];
  const data = slice.data ?? slice;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.items?.items)) return data.items.items;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  return [];
};

const collapseSpaces = (str) => String(str || "").replace(/\s+/g, " ").trim();

const formatDateShortEs = (d) => {
  if (!d) return "N/A";
  const x = dayjs(d);
  if (!x.isValid()) return String(d);
  return x.format("DD MMM YYYY HH:mm [hrs]");
};

// Animación "happy work" (misma de @ant-design/happy-work-theme que usa el
// botón Crear) aplicada a TODOS los botones de esta página vía HappyProvider.

const FORMA_ICON_SVG = {
  "Convenio": {
    viewBox: "0 0 640 512",
    path: "M0 383.9l64 .0404c17.75 0 32-14.29 32-32.03V128.3L0 128.3V383.9zM48 320.1c8.75 0 16 7.118 16 15.99c0 8.742-7.25 15.99-16 15.99S32 344.8 32 336.1C32 327.2 39.25 320.1 48 320.1zM348.8 64c-7.941 0-15.66 2.969-21.52 8.328L228.9 162.3C228.8 162.5 228.8 162.7 228.6 162.7C212 178.3 212.3 203.2 226.5 218.7c12.75 13.1 39.38 17.62 56.13 2.75C282.8 221.3 282.9 221.3 283 221.2l79.88-73.1c6.5-5.871 16.75-5.496 22.62 1c6 6.496 5.5 16.62-1 22.62l-26.12 23.87L504 313.7c2.875 2.496 5.5 4.996 7.875 7.742V127.1c-40.98-40.96-96.48-63.88-154.4-63.88L348.8 64zM334.6 217.4l-30 27.49c-29.75 27.11-75.25 24.49-101.8-4.371C176 211.2 178.1 165.7 207.3 138.9L289.1 64H282.5C224.7 64 169.1 87.08 128.2 127.9L128 351.8l18.25 .0369l90.5 81.82c27.5 22.37 67.75 18.12 90-9.246l18.12 15.24c15.88 12.1 39.38 10.5 52.38-5.371l31.38-38.6l5.374 4.498c13.75 11 33.88 9.002 45-4.748l9.538-11.78c11.12-13.75 9.036-33.78-4.694-44.93L334.6 217.4zM544 128.4v223.6c0 17.62 14.25 32.05 31.1 32.05L640 384V128.1L544 128.4zM592 352c-8.75 0-16-7.246-16-15.99c0-8.875 7.25-15.99 16-15.99S608 327.2 608 336.1C608 344.8 600.8 352 592 352z",
  },
  "Desistimiento": {
    viewBox: "0 0 640 512",
    path: "M274.7 304H173.3C77.61 304 0 381.6 0 477.3C0 496.5 15.52 512 34.66 512h378.7C432.5 512 448 496.5 448 477.3C448 381.6 370.4 304 274.7 304zM224 256c70.7 0 128-57.31 128-128S294.7 0 224 0C153.3 0 96 57.31 96 128S153.3 256 224 256zM616 200h-144C458.8 200 448 210.8 448 224s10.75 24 24 24h144C629.3 248 640 237.3 640 224S629.3 200 616 200z",
  },
  "Sentencia / Laudo favorable": {
    viewBox: "0 0 512 512",
    path: "M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM371.8 211.8C382.7 200.9 382.7 183.1 371.8 172.2C360.9 161.3 343.1 161.3 332.2 172.2L224 280.4L179.8 236.2C168.9 225.3 151.1 225.3 140.2 236.2C129.3 247.1 129.3 264.9 140.2 275.8L204.2 339.8C215.1 350.7 232.9 350.7 243.8 339.8L371.8 211.8z",
  },
  "Sentencia / Laudo desfavorable": {
    viewBox: "0 0 512 512",
    path: "M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM175 208.1L222.1 255.1L175 303C165.7 312.4 165.7 327.6 175 336.1C184.4 346.3 199.6 346.3 208.1 336.1L255.1 289.9L303 336.1C312.4 346.3 327.6 346.3 336.1 336.1C346.3 327.6 346.3 312.4 336.1 303L289.9 255.1L336.1 208.1C346.3 199.6 346.3 184.4 336.1 175C327.6 165.7 312.4 165.7 303 175L255.1 222.1L208.1 175C199.6 165.7 184.4 165.7 175 175C165.7 184.4 165.7 199.6 175 208.1V208.1z",
  },
  "Sobreseimiento": {
    viewBox: "0 0 576 512",
    path: "M0 64C0 28.65 28.65 0 64 0H224V128C224 145.7 238.3 160 256 160H384V198.6C310.1 219.5 256 287.4 256 368C256 427.1 285.1 479.3 329.7 511.3C326.6 511.7 323.3 512 320 512H64C28.65 512 0 483.3 0 448V64zM256 128V0L384 128H256zM576 368C576 447.5 511.5 512 432 512C352.5 512 288 447.5 288 368C288 288.5 352.5 224 432 224C511.5 224 576 288.5 576 368zM496 351.1H368C359.2 351.1 352 359.2 352 367.1C352 376.8 359.2 383.1 368 383.1H496C504.8 383.1 512 376.8 512 367.1C512 359.2 504.8 351.1 496 351.1z",
  },
  "Incompetencia": {
    viewBox: "0 0 512 512",
    path: "M424.1 287c-15.13-15.12-40.1-4.426-40.1 16.97V352H336L153.6 108.8C147.6 100.8 138.1 96 128 96H32C14.31 96 0 110.3 0 128s14.31 32 32 32h80l182.4 243.2C300.4 411.3 309.9 416 320 416h63.97v47.94c0 21.39 25.86 32.12 40.99 17l79.1-79.98c9.387-9.387 9.387-24.59 0-33.97L424.1 287zM336 160h47.97v48.03c0 21.39 25.87 32.09 40.1 16.97l79.1-79.98c9.387-9.391 9.385-24.59-.0013-33.97l-79.1-79.98c-15.13-15.12-40.99-4.391-40.99 17V96H320c-10.06 0-19.56 4.75-25.59 12.81L254 162.7L293.1 216L336 160zM112 352H32c-17.69 0-32 14.31-32 32s14.31 32 32 32h96c10.06 0 19.56-4.75 25.59-12.81l40.4-53.87L154 296L112 352z",
  },
  "Acumulación a otro expediente": {
    viewBox: "0 0 448 512",
    path: "M208 239.1H294.7C307 211.7 335.2 191.1 368 191.1C412.2 191.1 448 227.8 448 271.1C448 316.2 412.2 352 368 352C335.2 352 307 332.3 294.7 303.1H208C171.1 303.1 138.7 292.1 112 272V358.7C140.3 371 160 399.2 160 432C160 476.2 124.2 512 80 512C35.82 512 0 476.2 0 432C0 399.2 19.75 371 48 358.7V153.3C19.75 140.1 0 112.8 0 80C0 35.82 35.82 0 80 0C124.2 0 160 35.82 160 80C160 112.6 140.5 140.7 112.4 153.2C117 201.9 158.1 240 208 240V239.1zM80 103.1C93.25 103.1 104 93.25 104 79.1C104 66.74 93.25 55.1 80 55.1C66.75 55.1 56 66.74 56 79.1C56 93.25 66.75 103.1 80 103.1zM80 456C93.25 456 104 445.3 104 432C104 418.7 93.25 408 80 408C66.75 408 56 418.7 56 432C56 445.3 66.75 456 80 456zM368 247.1C354.7 247.1 344 258.7 344 271.1C344 285.3 354.7 295.1 368 295.1C381.3 295.1 392 285.3 392 271.1C392 258.7 381.3 247.1 368 247.1z",
  },
  "Caducidad": {
    viewBox: "0 0 384 512",
    path: "M352 0C369.7 0 384 14.33 384 32C384 49.67 369.7 64 352 64V74.98C352 117.4 335.1 158.1 305.1 188.1L237.3 256L305.1 323.9C335.1 353.9 352 394.6 352 437V448C369.7 448 384 462.3 384 480C384 497.7 369.7 512 352 512H32C14.33 512 0 497.7 0 480C0 462.3 14.33 448 32 448V437C32 394.6 48.86 353.9 78.86 323.9L146.7 256L78.86 188.1C48.86 158.1 32 117.4 32 74.98V64C14.33 64 0 49.67 0 32C0 14.33 14.33 0 32 0H352zM111.1 128H272C282.4 112.4 288 93.98 288 74.98V64H96V74.98C96 93.98 101.6 112.4 111.1 128zM111.1 384H272C268.5 378.7 264.5 373.7 259.9 369.1L192 301.3L124.1 369.1C119.5 373.7 115.5 378.7 111.1 384V384z",
  },
};

const pickNextEvent = (exp) => {
  // prioridad práctica (ajústala si quieres):
  // 1) término crítico más próximo (fecha_limite_contestacion o el capturado
  //    en la bitácora de Actuaciones — el que venza primero)
  // 2) audiencia conciliadora (warning)
  // 3) recepción demanda (info)
  const criticos = [];
  if (exp?.fecha_limite_contestacion) {
    criticos.push({ fecha: exp.fecha_limite_contestacion, tipo: "Límite contestación" });
  }
  if (exp?.actuacion_termino_fecha_limite) {
    criticos.push({ fecha: exp.actuacion_termino_fecha_limite, tipo: exp.actuacion_termino_tipo || "Término" });
  }
  if (criticos.length) {
    criticos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
    const c = criticos[0];
    return {
      eventoFecha: formatDateShortEs(c.fecha),
      eventoTipo: c.tipo,
      eventoKind: "red",
      eventoFechaRaw: c.fecha,
    };
  }
  if (exp?.fecha_audiencia_conciliadora) {
    return {
      eventoFecha: formatDateShortEs(exp.fecha_audiencia_conciliadora),
      eventoTipo: "Audiencia",
      eventoKind: "yellow",
      eventoFechaRaw: exp.fecha_audiencia_conciliadora,
    };
  }
  if (exp?.fecha_recepcion_demanda) {
    return {
      eventoFecha: formatDateShortEs(exp.fecha_recepcion_demanda),
      eventoTipo: "Recepción demanda",
      eventoKind: "blue",
      eventoFechaRaw: exp.fecha_recepcion_demanda,
    };
  }
  return {
    eventoFecha: "N/A",
    eventoTipo: "Sin fecha próxima",
    eventoKind: "gray",
    eventoFechaRaw: null,
  };
};

const mapExpedienteToRow = (exp) => {
  const id = Number(exp?.id);

  // Chip que refleja la etapa/estado actual (lo que filtra el expediente)
  const estatus = computeTribunalEstatus(exp);

  const actorName = exp?.nombre_trabajador || exp?.trabajador_nombre || exp?.actor || exp?.nombre_parte_actora || "";
  const razones = Array.isArray(exp?.razones_sociales)
    ? exp.razones_sociales
        .map((x) => collapseSpaces(x?.razon_social || ""))
        .filter(Boolean)
    : [];

  const contraName =
    razones.length > 0
      ? razones.join(" vs ")
      : (exp?.empresa_nombre || exp?.contraparte || exp?.corresponsal || "");
  const accion = exp?.accion_intentada || exp?.accion || "";

  const tribunal = exp?.nombre_autoridad || exp?.tribunal || "";
  const ubicacion = `${exp?.nombre_estado || ""}${exp?.nombre_estado && exp?.nombre_ciudad ? " / " : ""}${exp?.nombre_ciudad || ""}`.trim();

  const evento = pickNextEvent(exp);
  const titleParts = [actorName || "—"];
  if (contraName) titleParts.push(contraName);
  const caseTitle = titleParts.join(" vs ");

  // Aviso independiente de la etapa: no se refleja en el badge, solo en la
  // alerta morada de la tarjeta.
  const pendienteInformacion =
    exp?.carga_rapida === 1 || exp?.carga_rapida === true || exp?.carga_rapida === "1";

  return {
    id: Number.isFinite(id) ? id : null,
    expediente: exp?.numero_expediente || exp?.expediente_format || String(exp?.id || ""),
    actor: actorName || "—",
    contraparte: contraName || "—",
    caseTitle,
    accion: accion || "—",
    estatus,
    pendienteInformacion,
    tribunal: tribunal || "—",
    ubicacion: ubicacion || "—",
    fechaContestacion: exp?.fecha_contestacion ? formatDateShortEs(exp.fecha_contestacion) : null,
    ...evento,
  };
};

function eventoBsColor(kind) {
  const map = { yellow: "warning", blue: "info", green: "success", red: "danger", gray: "secondary" };
  return map[kind] || "secondary";
}

function isEventoEstaSemana(fechaRaw) {
  if (!fechaRaw) return false;
  const d = dayjs(fechaRaw);
  if (!d.isValid()) return false;
  if (d.isBefore(dayjs(), "day")) return false;
  return d.isSame(dayjs(), "week");
}

function StatusPillTrib({ kind, label }) {
  return (
    <div style={{ width: 140, flexShrink: 0 }}>
      <DataBadge
        color={STATUS_PILL_HEX[kind] || kind}
        className="d-flex align-items-center justify-content-center gap-1 status-pill-badge"
        style={{ whiteSpace: "normal", width: "100%", boxSizing: "border-box", lineHeight: 1.3, textAlign: "center" }}
      >
        {label}
      </DataBadge>
    </div>
  );
}

const KPI_BG_VARIANT = { blue: "info", green: "success", gray: "secondary", yellow: "warning", red: "danger", orange: "orange", purple: "purple", primary: "primary" };

const KPI_ICON_COLORS = {
  primary: { bg: "#DCE9F5", fg: "#0A539A" },
  success: { bg: "#16A34A26", fg: "#16A34A" },
  secondary: { bg: "#F3F4F6", fg: "#6B7280" },
  warning: { bg: "#FEF3C7", fg: "#F59E0B" },
  danger: { bg: "#FEE2E2", fg: "#DC2626" },
  info: { bg: "#e6f4ff", fg: "#1677ff" },
  orange: { bg: "#FFEDD5", fg: "#EA580C" },
  purple: { bg: "#EDE9FE", fg: "#7C3AED" },
};

function KpiCardTrib({ iconSvg, iconBg, value, label, active = false, onClick }) {
  const variant = KPI_BG_VARIANT[iconBg] || "secondary";
  const iconColors = KPI_ICON_COLORS[variant] || KPI_ICON_COLORS.secondary;
  return (
    <Card
      role={onClick ? "button" : undefined}
      onClick={onClick}
      bodyStyle={{ display: "flex", alignItems: "center", gap: 12 }}
      style={{
        cursor: onClick ? "pointer" : "default",
        borderColor: active ? "#0A539A" : undefined,
        borderWidth: active ? 2 : undefined,
      }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center"
        style={{ width: 44, height: 44, background: iconColors.bg, color: iconColors.fg }}
      >
        {iconSvg}
      </span>
      <div>
        <div className="fs-4 fw-bold lh-1">{value}</div>
        <div className="small text-secondary">{label}</div>
      </div>
    </Card>
  );
}

function SubStatCard({ active, onClick, iconSvg, badgeText, badgeBg = "#FEE2E2", badgeFg = "#DC2626", iconBg = "#F3F4F6", iconFg = "#6B7280", value, label }) {
  return (
    <Card
      className={`laboral-stat-card ${active ? "laboral-stat-active" : ""}`}
      bodyStyle={{ padding: 0 }}
      onClick={onClick}
    >
      <div className="d-flex align-items-center gap-2">
        <div
          className="laboral-stat-icon d-inline-flex align-items-center justify-content-center"
          style={
            badgeText
              ? { width: 44, height: 44, background: badgeBg, color: badgeFg, fontSize: 15, fontWeight: 700 }
              : { width: 44, height: 44, background: iconBg, color: iconFg }
          }
        >
          {badgeText || iconSvg}
        </div>
        <div>
          <div className="laboral-stat-value">{value}</div>
          <div className="laboral-stat-label">{label}</div>
        </div>
      </div>
    </Card>
  );
}

function FooterPagerTrib({ total, totalPages, page, setPage }) {
  const ps = PAGE_SIZE || 5;
  const start = total === 0 ? 0 : (page - 1) * ps + 1;
  const end = Math.min(page * ps, total);
  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <small className="text-secondary">
        Mostrando <strong>{start}</strong> a <strong>{end}</strong> de <strong>{total}</strong> resultados
      </small>
      <AppPagination current={page} total={total} pageSize={ps} onChange={setPage} size="sm" />
    </div>
  );
}

// ===== Íconos SVG (Font Awesome, inline) reutilizados en toda la página =====
const ICONS = {
  vencido: (
    <path d="M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM175 208.1L222.1 255.1L175 303C165.7 312.4 165.7 327.6 175 336.1C184.4 346.3 199.6 346.3 208.1 336.1L255.1 289.9L303 336.1C312.4 346.3 327.6 346.3 336.1 336.1C346.3 327.6 346.3 312.4 336.1 303L289.9 255.1L336.1 208.1C346.3 199.6 346.3 184.4 336.1 175C327.6 165.7 312.4 165.7 303 175L255.1 222.1L208.1 175C199.6 165.7 184.4 165.7 175 175C165.7 184.4 165.7 199.6 175 208.1V208.1z" />
  ),
  file: (
    <path d="M256 0v128h128L256 0zM224 128L224 0H48C21.49 0 0 21.49 0 48v416C0 490.5 21.49 512 48 512h288c26.51 0 48-21.49 48-48V160h-127.1C238.3 160 224 145.7 224 128zM272 416h-160C103.2 416 96 408.8 96 400C96 391.2 103.2 384 112 384h160c8.836 0 16 7.162 16 16C288 408.8 280.8 416 272 416zM272 352h-160C103.2 352 96 344.8 96 336C96 327.2 103.2 320 112 320h160c8.836 0 16 7.162 16 16C288 344.8 280.8 352 272 352zM288 272C288 280.8 280.8 288 272 288h-160C103.2 288 96 280.8 96 272C96 263.2 103.2 256 112 256h160C280.8 256 288 263.2 288 272z" />
  ),
  gavel: (
    <path d="M172.5 131.1C186.8 117.5 194.6 98.61 194.6 79.53c0-16.75-6.031-33.7-18.22-47.11C163.7 18.59 145.4 10.98 126.1 10.98c-17.7 0-34.6 6.62-47.63 18.66L18.72 89.66C6.031 101.6 0 118 0 134.1c0 17.14 6.531 34.31 19.55 47.13c13.03 12.83 30.11 19.14 47.19 19.14c17.09 0 33.98-6.375 46.72-19.05L172.5 131.1zM528.6 322.2c-13.03-12.83-30.11-19.14-47.19-19.14c-17.09 0-33.98 6.375-46.72 19.05l-59.06 50.93c-14.28 13.55-22.06 32.44-22.06 51.53c0 16.75 6.031 33.7 18.22 47.11c13.03 13.83 31.33 21.44 49.66 21.44c17.7 0 34.6-6.62 47.63-18.66l60.72-60.02c12.69-11.98 18.72-28.39 18.72-44.48C548.6 352.2 542 335 528.6 322.2zM416 128h96V64h-96V0h-64v64h-96v64h96v64h64V128z" />
  ),
  layers: (
    <path d="M232.5 5.171C247.4-1.718 264.6-1.718 279.5 5.171L498.1 106.2C506.6 110.1 512 118.6 512 127.1C512 137.3 506.6 145.8 498.1 149.8L279.5 250.8C264.6 257.7 247.4 257.7 232.5 250.8L13.93 149.8C5.438 145.8 0 137.3 0 127.1C0 118.6 5.437 110.1 13.93 106.2L232.5 5.171zM498.1 234.2C506.6 238.1 512 246.6 512 255.1C512 265.3 506.6 273.8 498.1 277.8L279.5 378.8C264.6 385.7 247.4 385.7 232.5 378.8L13.93 277.8C5.438 273.8 0 265.3 0 255.1C0 246.6 5.437 238.1 13.93 234.2L67.13 209.6L219.1 279.8C242.5 290.7 269.5 290.7 292.9 279.8L444.9 209.6L498.1 234.2zM292.9 407.8L444.9 337.6L498.1 362.2C506.6 366.1 512 374.6 512 383.1C512 393.3 506.6 401.8 498.1 405.8L279.5 506.8C264.6 513.7 247.4 513.7 232.5 506.8L13.93 405.8C5.438 401.8 0 393.3 0 383.1C0 374.6 5.437 366.1 13.93 362.2L67.13 337.6L219.1 407.8C242.5 418.7 269.5 418.7 292.9 407.8V407.8z" />
  ),
};

function TableView({ pageRows, total, totalPages, page, setPage, navigate, saveFiltersToSession, location, canConsultar }) {
  const columns = [
    {
      title: "Expediente / Actor",
      key: "expediente",
      width: 260,
      render: (_, r) => (
        <>
          <div className="fw-semibold">{r.expediente}</div>
          <div className="small fw-semibold text-uppercase">{r.caseTitle}</div>
          {r.fechaContestacion && (
            <div className="small text-secondary">Contestada: {r.fechaContestacion}</div>
          )}
        </>
      ),
    },
    {
      title: "Estatus",
      key: "accion",
      render: (_, r) => (
        <>
          {r.accion && <div className="small mb-1">{r.accion}</div>}
          <StatusPillTrib kind={r.estatus.kind} label={r.estatus.label} />
        </>
      ),
    },
    {
      title: "Tribunal",
      key: "tribunal",
      render: (_, r) => (
        <>
          <div className="small" title={r.tribunal}>{r.tribunal}</div>
          <div className="small text-secondary">
            <svg className="me-1" viewBox="0 0 384 512" width="13" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
              <path d="M168.3 499.2C116.1 435 0 279.4 0 192C0 85.96 85.96 0 192 0C298 0 384 85.96 384 192C384 279.4 267 435 215.7 499.2C203.4 514.5 180.6 514.5 168.3 499.2H168.3zM192 256C227.3 256 256 227.3 256 192C256 156.7 227.3 128 192 128C156.7 128 128 156.7 128 192C128 227.3 156.7 256 192 256z" />
            </svg>
            {r.ubicacion}
          </div>
        </>
      ),
    },
    {
      title: "Próximo evento",
      key: "evento",
      render: (_, r) => (
        <div
          className={`exp-audiencia-highlight exp-audiencia-highlight--${eventoBsColor(r.eventoKind)}${
            isEventoEstaSemana(r.eventoFechaRaw) ? " exp-audiencia-highlight--soon" : ""
          }`}
        >
          <div className="exp-audiencia-highlight-icon-wrap">
            <svg
              className="exp-audiencia-highlight-icon"
              viewBox="0 0 448 512"
              width="17"
              height="17"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M160 32V64H288V32C288 14.33 302.3 0 320 0C337.7 0 352 14.33 352 32V64H400C426.5 64 448 85.49 448 112V160H0V112C0 85.49 21.49 64 48 64H96V32C96 14.33 110.3 0 128 0C145.7 0 160 14.33 160 32zM0 192H448V464C448 490.5 426.5 512 400 512H48C21.49 512 0 490.5 0 464V192zM80 256C71.16 256 64 263.2 64 272V368C64 376.8 71.16 384 80 384H176C184.8 384 192 376.8 192 368V272C192 263.2 184.8 256 176 256H80z" />
            </svg>
          </div>
          <div className="exp-audiencia-highlight-body">
            <div className="exp-audiencia-highlight-label">Próximo evento</div>
            <div className="exp-audiencia-highlight-value">{r.eventoFecha}</div>
            <div className="exp-audiencia-highlight-sub">{r.eventoTipo}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      align: "right",
      render: (_, r) => (
        <Button
          type="primary"
          block
          onClick={() => { if (canConsultar) { saveFiltersToSession(); navigate(`/materias/laboral/tribunal/${r.id}${location.search}`); } }}
          disabled={!canConsultar}
          title={!canConsultar ? "Sin permiso de consulta" : undefined}
        >
          <svg className="me-1" viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
            <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c17.67 0 32 14.33 32 32c0 17.67-14.33 32-32 32S224 177.7 224 160C224 142.3 238.3 128 256 128zM296 384h-80C202.8 384 192 373.3 192 360s10.75-24 24-24h16v-64H224c-13.25 0-24-10.75-24-24S210.8 224 224 224h32c13.25 0 24 10.75 24 24v88h16c13.25 0 24 10.75 24 24S309.3 384 296 384z" />
          </svg>
          Ver detalles
        </Button>
      ),
    },
  ];

  return (
    <Card bodyStyle={{ padding: 0 }}>
      <Table
        columns={columns}
        dataSource={pageRows}
        rowKey={(r) => r.expediente + r.id}
        pagination={false}
      />
      <div className="p-3" style={{ borderTop: "1px solid #eef2f7", background: "#fff" }}>
        <FooterPagerTrib total={total} totalPages={totalPages} page={page} setPage={setPage} />
      </div>
    </Card>
  );
}

function CardsView({ rows, total, totalPages, page, setPage, navigate, saveFiltersToSession, location, canConsultar }) {
  return (
    <>
      <Row gutter={[24, 24]}>
        {rows.map((r) => (
          <Col key={r.expediente + r.id} xs={24} md={12} xl={8} xxl={6}>
            <Card className="h-100 exp-card" bodyStyle={{ display: "flex", flexDirection: "column", gap: 8, height: "100%" }}>
              <div className="d-flex align-items-start justify-content-between gap-2">
                <div className="fw-semibold text-uppercase">{r.caseTitle}</div>
                <StatusPillTrib kind={r.estatus.kind} label={r.estatus.label} />
              </div>

              <div className="d-flex align-items-start gap-2 small">
                <svg
                  className="mt-1"
                  viewBox="0 0 384 512"
                  width="14"
                  height="14"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  {ICONS.file}
                </svg>
                <div>
                  <div className="text-secondary text-uppercase" style={{ fontSize: 11 }}>
                    Acción intentada
                  </div>
                  <div>{r.accion}</div>
                </div>
              </div>

              <hr className="my-1" />

              <div className="d-flex flex-column gap-1 small text-secondary">
                <div>
                  <svg
                    className="me-1"
                    viewBox="0 0 640 512"
                    width="14"
                    height="14"
                    fill="currentColor"
                    aria-hidden="true"
                    style={{ verticalAlign: "-1px" }}
                  >
                    {ICONS.gavel}
                  </svg>
                  {r.tribunal}
                </div>
                <div>
                  <svg
                    className="me-1"
                    viewBox="0 0 384 512"
                    width="13"
                    height="13"
                    fill="currentColor"
                    aria-hidden="true"
                    style={{ verticalAlign: "-1px" }}
                  >
                    <path d="M168.3 499.2C116.1 435 0 279.4 0 192C0 85.96 85.96 0 192 0C298 0 384 85.96 384 192C384 279.4 267 435 215.7 499.2C203.4 514.5 180.6 514.5 168.3 499.2H168.3zM192 256C227.3 256 256 227.3 256 192C256 156.7 227.3 128 192 128C156.7 128 128 156.7 128 192C128 227.3 156.7 256 192 256z" />
                  </svg>
                  {r.ubicacion}
                </div>
                <div>
                  <i className="bi bi-hash me-1" />
                  Expediente: <strong>{r.expediente}</strong>
                </div>
                {r.fechaContestacion && (
                  <div>
                    <i className="bi bi-envelope-check me-1" />
                    Contestada: <strong>{r.fechaContestacion}</strong>
                  </div>
                )}
              </div>

              <div className="mt-auto">
                <div
                  className={`exp-audiencia-highlight exp-audiencia-highlight--${eventoBsColor(r.eventoKind)} mb-2${
                    isEventoEstaSemana(r.eventoFechaRaw) ? " exp-audiencia-highlight--soon" : ""
                  }`}
                >
                  <div className="exp-audiencia-highlight-icon-wrap">
                    <svg
                      className="exp-audiencia-highlight-icon"
                      viewBox="0 0 448 512"
                      width="17"
                      height="17"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M160 32V64H288V32C288 14.33 302.3 0 320 0C337.7 0 352 14.33 352 32V64H400C426.5 64 448 85.49 448 112V160H0V112C0 85.49 21.49 64 48 64H96V32C96 14.33 110.3 0 128 0C145.7 0 160 14.33 160 32zM0 192H448V464C448 490.5 426.5 512 400 512H48C21.49 512 0 490.5 0 464V192zM80 256C71.16 256 64 263.2 64 272V368C64 376.8 71.16 384 80 384H176C184.8 384 192 376.8 192 368V272C192 263.2 184.8 256 176 256H80z" />
                    </svg>
                  </div>
                  <div className="exp-audiencia-highlight-body">
                    <div className="exp-audiencia-highlight-label">
                      Próximo evento
                    </div>
                    <div className="exp-audiencia-highlight-value">{r.eventoFecha}</div>
                    <div className="exp-audiencia-highlight-sub">{r.eventoTipo}</div>
                  </div>
                </div>

                {r.pendienteInformacion && (
                  <div className="exp-audiencia-highlight exp-audiencia-highlight--purple mb-2">
                    <div className="exp-audiencia-highlight-icon-wrap">
                      <svg
                        className="exp-audiencia-highlight-icon"
                        viewBox="0 0 384 512"
                        width="17"
                        height="17"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        {ICONS.file}
                      </svg>
                    </div>
                    <div className="exp-audiencia-highlight-body">
                      <div className="exp-audiencia-highlight-label">
                        Pendiente de información
                      </div>
                      <div className="exp-audiencia-highlight-sub">Expediente creado por carga rápida</div>
                    </div>
                  </div>
                )}

                <Button
                  type="primary"
                  block
                  onClick={() => { if (canConsultar) { saveFiltersToSession(); navigate(`/materias/laboral/tribunal/${r.id}${location.search}`); } }}
                  disabled={!canConsultar}
                  title={!canConsultar ? "Sin permiso de consulta" : undefined}
                >
                  <svg className="me-1" viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
                    <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c17.67 0 32 14.33 32 32c0 17.67-14.33 32-32 32S224 177.7 224 160C224 142.3 238.3 128 256 128zM296 384h-80C202.8 384 192 373.3 192 360s10.75-24 24-24h16v-64H224c-13.25 0-24-10.75-24-24S210.8 224 224 224h32c13.25 0 24 10.75 24 24v88h16c13.25 0 24 10.75 24 24S309.3 384 296 384z" />
                  </svg>
                  Ver detalles
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      <div className="mt-3">
        <FooterPagerTrib total={total} totalPages={totalPages} page={page} setPage={setPage} />
      </div>
    </>
  );
}

const EXPORT_MENU_TONES = {
  primary: { bg: "#DCE9F5", fg: "#0A539A" },
  green: { bg: "#DCFCE7", fg: "#16A34A" },
};

function ExportMenuRow({ viewBox, path, tone, title, subtitle }) {
  const colors = EXPORT_MENU_TONES[tone] || EXPORT_MENU_TONES.primary;
  const [, , vbW, vbH] = viewBox.split(" ").map(Number);
  const height = 16;
  const width = Math.round((vbW / vbH) * height);
  return (
    <div className="laboral-export-menu-row">
      <span
        className="laboral-export-menu-icon"
        style={{ background: colors.bg, color: colors.fg }}
      >
        <svg viewBox={viewBox} width={width} height={height} fill="currentColor" aria-hidden="true">
          <path d={path} />
        </svg>
      </span>
      <div className="laboral-export-menu-text">
        <div className="laboral-export-menu-title">{title}</div>
        <div className="laboral-export-menu-subtitle">{subtitle}</div>
      </div>
    </div>
  );
}

export default function TribunalTablePage() {
  const dispatch = useDispatch();

  const { perm } = usePermisos() || { perm: () => true };
  const canConsultar = perm("tribunal", "consultar");
  const canInsertar = perm("tribunal", "insertar");

  const [view, setView] = useState("cards"); // "cards" | "table"
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [exportPreviewOpen, setExportPreviewOpen] = useState(false);
  const [exportPreviewHtml, setExportPreviewHtml] = useState("");

  // lee ?ciudad= de la URL para pre-filtrar desde el home
  const [searchParams] = useSearchParams();
  const _initialCiudad = (() => {
    const v = searchParams.get("ciudad");
    if (!v) return ALL;
    const n = Number(v);
    return Number.isFinite(n) ? n : ALL;
  })();

  // selects base (igual que tu page real)
  const [selectedEstadoId, setSelectedEstadoId] = useState(ALL);
  const [selectedCiudadId, setSelectedCiudadId] = useState(_initialCiudad);

  // autoridad
  const [selectedAutoridadId, setSelectedAutoridadId] = useState(ALL);
  const [selectedAutoridadData, setSelectedAutoridadData] = useState(null);

  const tribRestoreRef = useRef(undefined);

  // Leer estado y autoridad desde URL al montar (back-navigation)
  useEffect(() => {
    const e = searchParams.get("estado");
    const a = searchParams.get("autoridad");
    const en = e ? Number(e) : null;
    const an = a ? Number(a) : null;
    if (en && Number.isFinite(en)) setSelectedEstadoId(en);
    if (an && Number.isFinite(an)) setSelectedAutoridadId(an);
  }, []);

  // filtros UI
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("activos"); // siempre cae en Activos
  const [dateRange, setDateRange] = useState(null);
  const [searchDebounced, setSearchDebounced] = useState("");
  const [sortOption, setSortOption] = useState("default");

  // paginado
  const rowsPerPage = PAGE_SIZE || 5;
  const [page, setPage] = useState(1);

  // sub-filtros de cards KPI
  const [audienciaSubFilter, setAudienciaSubFilter] = useState("todos");
  const [terminoSubFilter, setTerminoSubFilter] = useState("todos");
  const [concluidosSubFilter, setConcluidosSubFilter] = useState("todos");
  const [pendienteInfoFilter, setPendienteInfoFilter] = useState(false);

  // slices reales
  const tribunalSlice = useSelector((state) => state.tribunal || {});
  const autoridadesSlice = useSelector((state) => state.autoridades || {});

  // catálogos reales
  const {
    estadosOptions,
    estadoSeleccionado,
    ciudadSeleccionada,
    ciudadesById,
  } = useLaboralCatalogos(selectedEstadoId, selectedCiudadId, { modulo: "tribunal" });

  // Ciudad por default según lo asignado al usuario (1 ciudad -> esa; varias
  // -> San Luis Río Colorado si está entre ellas, si no la primera alfabética).
  // No aplica si ya hay filtros guardados de sesión (back-navigation) o venía
  // ciudad por la URL.
  const [hadSavedFilters] = useState(() => {
    try { return !!sessionStorage.getItem("trib_filters"); } catch { return false; }
  });
  const { defaultCiudad, loaded: ciudadUsuarioLoaded } = useDefaultCiudadUsuario();
  const defaultCiudadAppliedRef = useRef(false);
  // Se vuelve true en el MISMO efecto (y por lo tanto en el MISMO render)
  // que ya deja selectedEstadoId/selectedCiudadId con sus valores finales,
  // para que filtrosBusqueda nunca se calcule con ALL/ALL en un render
  // intermedio y dispare una llamada API de más con parámetros vacíos.
  const [defaultResolved, setDefaultResolved] = useState(false);

  // Evita el "parpadeo" del select (Todos los estados -> Sonora / Todas las
  // ciudades -> San Luis Río Colorado) y la llamada API duplicada con
  // parámetros vacíos: si no hay filtros guardados ni ciudad en la URL,
  // esperamos a conocer y aplicar la ciudad default del usuario.
  const filtersReady = hadSavedFilters || _initialCiudad !== ALL || defaultResolved;

  useEffect(() => {
    if (defaultCiudadAppliedRef.current) return;
    if (hadSavedFilters) {
      defaultCiudadAppliedRef.current = true;
      return;
    }
    if (!ciudadUsuarioLoaded) return;
    if (!defaultCiudad) {
      defaultCiudadAppliedRef.current = true;
      setDefaultResolved(true);
      return;
    }
    if (!ciudadesById || Object.keys(ciudadesById).length === 0) return;

    if (selectedEstadoId !== ALL || selectedCiudadId !== ALL) {
      defaultCiudadAppliedRef.current = true;
      setDefaultResolved(true);
      return;
    }

    defaultCiudadAppliedRef.current = true;
    const idCiudad = Number(defaultCiudad.id_ciudad);
    if (!Number.isFinite(idCiudad)) {
      setDefaultResolved(true);
      return;
    }

    setSelectedCiudadId(idCiudad);
    const city = ciudadesById?.[String(idCiudad)] || ciudadesById?.[idCiudad];
    const idEstado = city?.id_estado ?? city?.idEstado ?? null;
    if (idEstado !== undefined && idEstado !== null && String(idEstado) !== "") {
      setSelectedEstadoId(Number(idEstado));
    }
    setPage(1);
    setDefaultResolved(true);
  }, [hadSavedFilters, ciudadUsuarioLoaded, defaultCiudad, ciudadesById, selectedEstadoId, selectedCiudadId]);

  // carga autoridades (igual que tu page real)
  useEffect(() => {
    dispatch(actionAutoridadesTribunalGet({}));
  }, [dispatch]);

  const autoridadesItems = useMemo(() => coerceItems(autoridadesSlice), [autoridadesSlice]);

  const estadosOptionsFinal = useMemo(() => {
    const base = Array.isArray(estadosOptions) ? estadosOptions : [];
    return [{ label: "Todos los estados", value: ALL }, ...base];
  }, [estadosOptions]);

  const ciudadesOptionsFinal = useMemo(() => {
    // Base: TODAS las ciudades conocidas (no depende de tener un estado elegido)
    const baseAll = Object.values(ciudadesById || {}).map((c) => ({
      label: c.nombre ?? c.code ?? `Ciudad ${c.id}`,
      value: c.id,
    }));

    // Si hay estado elegido, acota a las ciudades de ese estado
    const byEstado =
      selectedEstadoId === ALL
        ? baseAll
        : baseAll.filter((o) => {
            const city = ciudadesById?.[String(o.value)] || ciudadesById?.[Number(o.value)];
            const idEstado = city?.id_estado ?? city?.idEstado ?? null;
            return Number(idEstado) === Number(selectedEstadoId);
          });

    return [{ label: "Todas las ciudades", value: ALL }, ...byEstado];
  }, [ciudadesById, selectedEstadoId]);

  // Autoridad: filtrada en cascada -> si hay ciudad elegida, solo esa ciudad;
  // si solo hay estado elegido (sin ciudad), todas las autoridades de ese estado;
  // si no hay nada elegido, catálogo completo.
  const autoridadOptions = useMemo(() => {
    const base = (autoridadesItems || [])
      .filter((au) => {
        if (selectedCiudadId !== ALL) {
          return Number(au?.id_ciudad || 0) === Number(selectedCiudadId);
        }
        if (selectedEstadoId !== ALL) {
          const city = ciudadesById?.[String(au?.id_ciudad)] || ciudadesById?.[Number(au?.id_ciudad)];
          const idEstado = city?.id_estado ?? city?.idEstado ?? null;
          return Number(idEstado) === Number(selectedEstadoId);
        }
        return true;
      })
      .map((au) => ({
        label: collapseSpaces(au?.nombre || ""),
        value: au?.id,
        data: au,
      }))
      .filter((o) => o.label);

    return [{ label: "Todas las autoridades", value: ALL, data: null }, ...base];
  }, [autoridadesItems, selectedCiudadId, selectedEstadoId, ciudadesById]);

  // si por alguna razón llega null, lo normalizas a ALL
  useEffect(() => {
    if (selectedEstadoId == null) {
      setSelectedEstadoId(ALL);
      setSelectedCiudadId(ALL);
      setSelectedAutoridadId(ALL);
      setSelectedAutoridadData(null);
      setPage(1);
    }
  }, [selectedEstadoId]);

  // si cambias estado y la ciudad ya no es válida, fuerza ALL
  useEffect(() => {
    const base = Array.isArray(ciudadesOptionsFinal) ? ciudadesOptionsFinal : [];
    if (!base.length) return;

    const exists = base.some((o) => String(o.value) === String(selectedCiudadId));
    if (exists) return;

    setSelectedCiudadId(ALL);
    setSelectedAutoridadId(ALL);
    setSelectedAutoridadData(null);
    setPage(1);
  }, [ciudadesOptionsFinal, selectedCiudadId]);

  // autoridad: si la seleccion no existe en la lista, fuerza ALL (no "primera")
  useEffect(() => {
    const disponibles = Array.isArray(autoridadOptions) ? autoridadOptions : [];
    if (!disponibles.length) return;

    const exists = disponibles.some((o) => String(o.value) === String(selectedAutoridadId));
    if (exists) return;

    setSelectedAutoridadId(ALL);
    setSelectedAutoridadData(null);
    setPage(1);
  }, [autoridadOptions, selectedAutoridadId]);

  const handleChangeEstado = (value) => {
    const v = value ?? ALL;
    setSelectedEstadoId(v);
    setSelectedCiudadId(ALL);
    setSelectedAutoridadId(ALL);
    setSelectedAutoridadData(null);
    setPage(1);
  };

  const handleChangeCiudad = (value) => {
    const v = value ?? ALL;
    setSelectedCiudadId(v);

    // ✅ autorellena ESTADO a partir de la ciudad elegida (pero NO autoridad,
    // porque una ciudad puede tener varias autoridades)
    if (v !== ALL) {
      const city = ciudadesById?.[String(v)] || ciudadesById?.[Number(v)];
      const idEstado = city?.id_estado ?? city?.idEstado ?? null;
      if (idEstado !== undefined && idEstado !== null && String(idEstado) !== "") {
        setSelectedEstadoId(Number(idEstado));
      }
    }

    setSelectedAutoridadId(ALL);
    setSelectedAutoridadData(null);
    setPage(1);
  };

  // tipoConfig real
  const tipoConfig = (laboralTiposConfig && laboralTiposConfig["tribunal"]) || { key: "tribunal", title: "Tribunal", shortLabel: "Tribunal" };

  // filtros backend (igual que tu page real)
  const filtrosBusqueda = useMemo(() => {
    if (!selectedEstadoId || !selectedCiudadId) return null;

    const expediente = collapseSpaces(searchDebounced);

    return {
      id_estado: selectedEstadoId !== ALL ? Number(selectedEstadoId) : undefined,
      id_ciudad: selectedCiudadId !== ALL ? Number(selectedCiudadId) : undefined,
      id_autoridad: selectedAutoridadId !== ALL ? Number(selectedAutoridadId) : undefined,
      search: expediente || undefined,
      fecha_inicio: dateRange?.[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : undefined,
      fecha_fin: dateRange?.[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : undefined,
    };
  }, [
    selectedEstadoId,
    selectedCiudadId,
    selectedAutoridadId,
    searchDebounced,
    dateRange,
  ]);
  const navigate = useNavigate();
  const location = useLocation();

  const saveFiltersToSession = () => {
    const payload = {
      estado: selectedEstadoId,
      ciudad: selectedCiudadId,
      autoridad: selectedAutoridadId,
      search,
      statusFilter,
      audienciaSubFilter,
      terminoSubFilter,
      concluidosSubFilter,
      pendienteInfoFilter,
      page,
      dateRange: dateRange
        ? [dateRange[0]?.toISOString?.() ?? null, dateRange[1]?.toISOString?.() ?? null]
        : null,
    };
    sessionStorage.setItem("trib_filters", JSON.stringify(payload));
  };

  const handleCrearExpediente = () => {
    if (!selectedEstadoId || !selectedCiudadId || !selectedAutoridadId) return;

    const nombreIdent = selectedAutoridadData?.nombre_identificacion_ciudad || "";

    navigate(`/materias/laboral/tribunal/crear/`, {
      state: {
        estado: selectedEstadoId,
        ciudad: selectedCiudadId,
        idAutoridad: selectedAutoridadId,
        nombreIdentificacionCiudad: nombreIdent || null,
      },
    });
  };

  const filtrosKey = useMemo(() => JSON.stringify(filtrosBusqueda || {}), [filtrosBusqueda]);
  const lastKeyRef = useRef("");

  useEffect(() => {
    // Mientras se resuelve la ciudad default del usuario no hay nada firme
    // que consultar todavía: evita disparar una llamada con id_estado/id_ciudad
    // vacíos que luego se vuelve a disparar ya con los valores reales.
    if (!filtersReady) return;
    if (!filtrosBusqueda) return;

    if (lastKeyRef.current === filtrosKey) return;
    lastKeyRef.current = filtrosKey;

    dispatch(actionTribunalCards(filtrosBusqueda));
  }, [dispatch, filtrosKey, filtrosBusqueda, filtersReady]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Restaurar filtros guardados cuando ciudadesById cargue (back-navigation)
  useEffect(() => {
    if (tribRestoreRef.current === undefined) {
      const saved = sessionStorage.getItem("trib_filters");
      sessionStorage.removeItem("trib_filters");
      tribRestoreRef.current = saved || null;
    }
    if (!tribRestoreRef.current) return;
    if (!ciudadesById || Object.keys(ciudadesById).length === 0) return;

    const raw = tribRestoreRef.current;
    tribRestoreRef.current = null;

    try {
      const f = JSON.parse(raw);
      if (f.estado != null && Number.isFinite(Number(f.estado))) setSelectedEstadoId(Number(f.estado) || ALL);
      if (f.ciudad != null && Number.isFinite(Number(f.ciudad))) setSelectedCiudadId(Number(f.ciudad) || ALL);
      if (f.autoridad != null && Number.isFinite(Number(f.autoridad))) setSelectedAutoridadId(Number(f.autoridad) || ALL);
      if (f.search !== undefined) setSearch(f.search || "");
      if (f.statusFilter) setStatusFilter(f.statusFilter);
      if (f.audienciaSubFilter) setAudienciaSubFilter(f.audienciaSubFilter);
      if (f.terminoSubFilter) setTerminoSubFilter(f.terminoSubFilter);
      if (f.concluidosSubFilter) setConcluidosSubFilter(f.concluidosSubFilter);
      if (f.pendienteInfoFilter !== undefined) setPendienteInfoFilter(!!f.pendienteInfoFilter);
      if (f.page && Number.isFinite(Number(f.page))) setPage(Number(f.page));
      if (Array.isArray(f.dateRange) && f.dateRange[0]) {
        const d0 = dayjs(f.dateRange[0]);
        const d1 = dayjs(f.dateRange[1]);
        if (d0.isValid() && d1.isValid()) setDateRange([d0, d1]);
      }
    } catch {
      // legacy URLSearchParams format
      const sp = new URLSearchParams(raw);
      const en = Number(sp.get("estado"));
      const cn = Number(sp.get("ciudad"));
      const an = Number(sp.get("autoridad"));
      if (en && Number.isFinite(en)) setSelectedEstadoId(en);
      if (cn && Number.isFinite(cn)) setSelectedCiudadId(cn);
      if (an && Number.isFinite(an)) setSelectedAutoridadId(an);
    }
  }, [ciudadesById]);

  // tu lógica real de filtro/paginado (hook)
  const {
    statusCounts,
    filteredExpedientes,
    paginatedExpedientes,
    totalExp,
  } = useTribunalProcedimiento({
    concSlice: tribunalSlice,
    search,
    statusFilter: pendienteInfoFilter ? "pendienteInformacion" : statusFilter,
    audienciaSubFilter,
    terminoSubFilter,
    concluidosSubFilter,
    dateRange,
    sortOption,
    currentPage: page,
    pageSize: rowsPerPage,
    setCurrentPage: setPage,
  });

  // mapeo a tu formato de tarjetas/tabla (SIN tocar el diseño)
  const rows = useMemo(
    () => (paginatedExpedientes || []).map(mapExpedienteToRow),
    [paginatedExpedientes]
  );
  const total = totalExp || 0;

  const totalPages = Math.max(1, Math.ceil((total || 0) / rowsPerPage));

  const handleExportNow = () => {
    const list = Array.isArray(filteredExpedientes) ? filteredExpedientes : [];
    const now = new Date();
    const y = now.getFullYear();
    const periodFrom = dateRange?.[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : `${y}-01-01`;
    const periodTo   = dateRange?.[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : `${y}-12-31`;
    const ciudadesLabel = ciudadSeleccionada?.nombre || "Todas";
    const nombreBusqueda = search || "Todos";
    const html = previewTribunalReportPdf({ items: list, periodFrom, periodTo, ciudadesLabel, nombreBusqueda });
    setExportPreviewHtml(html);
    setExportPreviewOpen(true);
  };

  // Evita el "parpadeo" del select de estado/ciudad (Todos -> Sonora / Todas
  // las ciudades -> San Luis Río Colorado): no pintamos el panel de filtros
  // hasta saber la ciudad default del usuario.
  if (!filtersReady) {
    return (
      <ConfigProvider locale={esES}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
          <Spin size="large" />
        </div>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider locale={esES}>
    <HappyProvider>
    <div>
      <Modal
        open={exportPreviewOpen}
        onCancel={() => setExportPreviewOpen(false)}
        centered
        width={1140}
        title="Previsualización"
        footer={[
          <Button key="close" onClick={() => setExportPreviewOpen(false)}>
            Cerrar
          </Button>,
          <Button
            key="export"
            type="primary"
            onClick={() => {
              const list = Array.isArray(filteredExpedientes) ? filteredExpedientes : [];
              const now = new Date();
              const y = now.getFullYear();
              const periodFrom = dateRange?.[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : `${y}-01-01`;
              const periodTo   = dateRange?.[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : `${y}-12-31`;
              const ciudadesLabel = ciudadSeleccionada?.nombre || "Todas";
              const nombreBusqueda = search || "Todos";
              printTribunalReportPdf({ items: list, periodFrom, periodTo, ciudadesLabel, nombreBusqueda });
            }}
          >
            Exportar PDF
          </Button>,
        ]}
      >
        <div style={{ height: "72vh", border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <iframe
            title="preview-tribunal"
            style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
            srcDoc={exportPreviewHtml}
          />
        </div>
      </Modal>

      <main className="trib-main">
        <div className="trib-wrap">
          {/* PANEL FILTROS */}
          <div className="laboral-filters-panel">
            <div className="laboral-panel-header">
              <h1 className="laboral-panel-title">{tipoConfig.title} · Materia laboral</h1>
              {estadoSeleccionado && ciudadSeleccionada ? (
                <p className="laboral-panel-subtitle">
                  Expedientes de {tipoConfig.shortLabel.toLowerCase()} en <b>{estadoSeleccionado.nombre}</b> /{" "}
                  <b>{ciudadSeleccionada.nombre}</b>.
                </p>
              ) : (
                <p className="laboral-panel-subtitle">
                  Selecciona un estado y una ciudad para ver los expedientes.
                </p>
              )}
            </div>

            {/* Fila 1: Estado / Ciudad / Autoridad */}
            <div className="laboral-filter-row-1">
              <div className="laboral-field">
                <div className="laboral-field-label">ESTADO</div>
                <Select
                  className="laboral-control"
                  value={selectedEstadoId}
                  options={estadosOptionsFinal}
                  onChange={handleChangeEstado}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>

              <div className="laboral-field">
                <div className="laboral-field-label">CIUDAD</div>
                <Select
                  className="laboral-control"
                  value={selectedCiudadId}
                  options={ciudadesOptionsFinal}
                  onChange={handleChangeCiudad}
                  disabled={!selectedEstadoId}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>

              <div className="laboral-field">
                <div className="laboral-field-label">AUTORIDAD</div>
                <Select
                  className="laboral-control"
                  value={selectedAutoridadId}
                  options={autoridadOptions}
                  onChange={(value, option) => {
                    setSelectedAutoridadId(value ?? ALL);
                    setSelectedAutoridadData(option?.data || null);
                    setPage(1);
                  }}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>
            </div>

            {/* Fila 2: Buscador (izq, ancho) + Fecha (centro) + Botones (der) */}
            <div className="laboral-filter-row-2">
              <div className="laboral-field laboral-field--search">
                <div className="laboral-field-label">BUSCADOR</div>
                <Input
                  className="laboral-search-modern__input"
                  prefix={<SearchOutlined className="laboral-search-modern__icon" />}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por expediente, empresa..."
                />
              </div>

              <div className="laboral-field laboral-field--dates">
                <div className="laboral-field-label">FECHA DE PRESENTACIÓN DEMANDA</div>
                <div className="laboral-date-range">
                  <ScrollDatePicker
                    value={dateRange?.[0] ? dayjs(dateRange[0]) : null}
                    format="YYYY-MM-DD"
                    placeholder="Inicio"
                    allowClear
                    onChange={(d) => setDateRange((prev) => [d || null, prev?.[1] ?? null])}
                  />
                  <span className="laboral-date-range-sep">–</span>
                  <ScrollDatePicker
                    value={dateRange?.[1] ? dayjs(dateRange[1]) : null}
                    format="YYYY-MM-DD"
                    placeholder="Fin"
                    allowClear
                    onChange={(d) => setDateRange((prev) => [prev?.[0] ?? null, d || null])}
                  />
                </div>
              </div>

              <div className="laboral-field laboral-field--actions">
                <div className="laboral-field-label">&nbsp;</div>
                <div className="laboral-actions">
                  <Button
                    onClick={() => {
                      setSearch("");
                      setDateRange(null);
                    }}
                    className="laboral-btn-clean"
                  >
                    Limpiar
                  </Button>
                  {canInsertar && (
                    <Button
                      type="primary"
                      onClick={handleCrearExpediente}
                      disabled={!selectedAutoridadId}
                      className="laboral-btn-create"
                      icon={
                        <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                          <path d="M432 256c0 17.69-14.33 32.01-32 32.01H256v144c0 17.69-14.33 31.99-32 31.99s-32-14.3-32-31.99v-144H48c-17.67 0-32-14.32-32-32.01s14.33-31.99 32-31.99H192v-144c0-17.69 14.33-32.01 32-32.01s32 14.32 32 32.01v144h144C417.7 224 432 238.3 432 256z" />
                        </svg>
                      }
                    >
                      Crear
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* KPI cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <KpiCardTrib
                iconBg="primary"
                iconSvg={
                  <svg viewBox="0 0 576 512" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="M147.8 192H480V144C480 117.5 458.5 96 432 96h-160l-64-64h-160C21.49 32 0 53.49 0 80v328.4l90.54-181.1C101.4 205.6 123.4 192 147.8 192zM543.1 224H147.8C135.7 224 124.6 230.8 119.2 241.7L0 480h447.1c12.12 0 23.2-6.852 28.62-17.69l96-192C583.2 249 567.7 224 543.1 224z" />
                  </svg>
                }
                value={String(statusCounts?.activos || 0)}
                label="Activos"
                active={statusFilter === "activos"}
                onClick={() => {
                  const next = statusFilter === "activos" ? "todos" : "activos";
                  setStatusFilter(next);
                  setTerminoSubFilter("todos");
                  setAudienciaSubFilter("todos");
                  setConcluidosSubFilter("todos");
                  setPendienteInfoFilter(false);
                  setPage(1);
                }}
              />
            </Col>
            <Col xs={24} md={6}>
              <KpiCardTrib
                iconBg="red"
                iconSvg={
                  <svg viewBox="0 0 512 512" width="18" height="18" fill="currentColor" aria-hidden="true">
                    {ICONS.vencido}
                  </svg>
                }
                value={String(statusCounts?.terminosCriticos || 0)}
                label="Términos Críticos"
                active={statusFilter === "conLimite"}
                onClick={() => {
                  const next = statusFilter === "conLimite" ? "todos" : "conLimite";
                  setStatusFilter(next);
                  if (next !== "conLimite") setTerminoSubFilter("todos");
                  setAudienciaSubFilter("todos");
                  setConcluidosSubFilter("todos");
                  setPendienteInfoFilter(false);
                  setPage(1);
                }}
              />
            </Col>
            <Col xs={24} md={6}>
              <KpiCardTrib
                iconBg="yellow"
                iconSvg={
                  <svg viewBox="0 0 448 512" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="M160 32V64H288V32C288 14.33 302.3 0 320 0C337.7 0 352 14.33 352 32V64H400C426.5 64 448 85.49 448 112V160H0V112C0 85.49 21.49 64 48 64H96V32C96 14.33 110.3 0 128 0C145.7 0 160 14.33 160 32zM0 192H448V464C448 490.5 426.5 512 400 512H48C21.49 512 0 490.5 0 464V192zM80 256C71.16 256 64 263.2 64 272V368C64 376.8 71.16 384 80 384H176C184.8 384 192 376.8 192 368V272C192 263.2 184.8 256 176 256H80z" />
                  </svg>
                }
                value={String(statusCounts?.proximasAudiencias || 0)}
                label="Próximas Audiencias"
                active={statusFilter === "conAudiencia"}
                onClick={() => {
                  const next = statusFilter === "conAudiencia" ? "todos" : "conAudiencia";
                  setStatusFilter(next);
                  if (next !== "conAudiencia") setAudienciaSubFilter("todos");
                  setTerminoSubFilter("todos");
                  setConcluidosSubFilter("todos");
                  setPendienteInfoFilter(false);
                  setPage(1);
                }}
              />
            </Col>
            <Col xs={24} md={6}>
              <KpiCardTrib
                iconBg="green"
                iconSvg={
                  <svg viewBox="0 0 512 512" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM371.8 211.8C382.7 200.9 382.7 183.1 371.8 172.2C360.9 161.3 343.1 161.3 332.2 172.2L224 280.4L179.8 236.2C168.9 225.3 151.1 225.3 140.2 236.2C129.3 247.1 129.3 264.9 140.2 275.8L204.2 339.8C215.1 350.7 232.9 350.7 243.8 339.8L371.8 211.8z" />
                  </svg>
                }
                value={String(statusCounts?.concluidos || 0)}
                label="Concluidos"
                active={statusFilter === "concluidos"}
                onClick={() => {
                  const next = statusFilter === "concluidos" ? "todos" : "concluidos";
                  setStatusFilter(next);
                  if (next !== "concluidos") setConcluidosSubFilter("todos");
                  setAudienciaSubFilter("todos");
                  setTerminoSubFilter("todos");
                  setPendienteInfoFilter(false);
                  setPage(1);
                }}
              />
            </Col>
          </Row>

          {/* Subfiltros de Términos Críticos */}
          <div className={`laboral-substats-wrap ${statusFilter === "conLimite" ? "is-open" : ""}`}>
            <div className="laboral-stats-row laboral-substats-row">
              {[
                { key: "etapa5", num: 5, label: "Etapa 5 · Contestación",                count: statusCounts?.terminosEtapa5 ?? 0 },
                { key: "etapa6", num: 6, label: "Etapa 6 · Reconvención",                count: statusCounts?.terminosEtapa6 ?? 0 },
                { key: "etapa7", num: 7, label: "Etapa 7 · Réplica",                     count: statusCounts?.terminosEtapa7 ?? 0 },
                { key: "etapa8", num: 8, label: "Etapa 8 · Contrarréplica",              count: statusCounts?.terminosEtapa8 ?? 0 },
                { key: "etapa9", num: 9, label: "Etapa 9 · Integración de la litis",     count: statusCounts?.terminosEtapa9 ?? 0 },
              ].map(({ key, num, label, count }) => (
                <SubStatCard
                  key={key}
                  active={terminoSubFilter === key}
                  onClick={() => { setTerminoSubFilter((p) => p === key ? "todos" : key); setPage(1); }}
                  badgeText={num}
                  value={count}
                  label={label}
                />
              ))}
              <SubStatCard
                active={terminoSubFilter === "todos"}
                onClick={() => { setTerminoSubFilter("todos"); setPage(1); }}
                iconSvg={
                  <svg viewBox="0 0 512 512" width="16" height="16" fill="currentColor" aria-hidden="true">
                    {ICONS.layers}
                  </svg>
                }
                value={statusCounts?.terminosCriticos ?? 0}
                label="Todos"
              />
            </div>
          </div>

          {/* Subfiltros de Próximas Audiencias */}
          <div className={`laboral-substats-wrap ${statusFilter === "conAudiencia" ? "is-open" : ""}`}>
            <div className="laboral-stats-row laboral-substats-row">
              <SubStatCard
                active={audienciaSubFilter === "etapa10"}
                onClick={() => { setAudienciaSubFilter((p) => p === "etapa10" ? "todos" : "etapa10"); setPage(1); }}
                badgeText={10}
                badgeBg="#FEF3C7"
                badgeFg="#F59E0B"
                value={statusCounts?.proximasEtapa10 ?? 0}
                label="Etapa 10 · Audiencia Conciliatoria"
              />
              <SubStatCard
                active={audienciaSubFilter === "etapa11"}
                onClick={() => { setAudienciaSubFilter((p) => p === "etapa11" ? "todos" : "etapa11"); setPage(1); }}
                badgeText={11}
                badgeBg="#FEF3C7"
                badgeFg="#F59E0B"
                value={statusCounts?.proximasEtapa11 ?? 0}
                label="Etapa 11 · Audiencia Preliminar"
              />
              <SubStatCard
                active={audienciaSubFilter === "etapa12"}
                onClick={() => { setAudienciaSubFilter((p) => p === "etapa12" ? "todos" : "etapa12"); setPage(1); }}
                badgeText={12}
                badgeBg="#FEF3C7"
                badgeFg="#F59E0B"
                value={statusCounts?.proximasEtapa12 ?? 0}
                label="Etapa 12 · Audiencia de Juicio"
              />
              <SubStatCard
                active={audienciaSubFilter === "todos"}
                onClick={() => { setAudienciaSubFilter("todos"); setPage(1); }}
                iconSvg={
                  <svg viewBox="0 0 512 512" width="16" height="16" fill="currentColor" aria-hidden="true">
                    {ICONS.layers}
                  </svg>
                }
                value={statusCounts?.proximasAudiencias ?? 0}
                label="Todos"
              />
            </div>
          </div>

          {/* Subfiltros de Concluidos */}
          <div className={`laboral-substats-wrap ${statusFilter === "concluidos" ? "is-open" : ""}`}>
            <div className="laboral-stats-row laboral-substats-row">
              {[
                "Convenio",
                "Desistimiento",
                "Sentencia / Laudo favorable",
                "Sentencia / Laudo desfavorable",
                "Caducidad",
                "Sobreseimiento",
                "Incompetencia",
                "Acumulación a otro expediente",
              ].map((forma) => (
                <SubStatCard
                  key={forma}
                  active={concluidosSubFilter === forma}
                  onClick={() => { setConcluidosSubFilter((p) => p === forma ? "todos" : forma); setPage(1); }}
                  iconBg="#16A34A26"
                  iconFg="#16A34A"
                  iconSvg={
                    <svg viewBox={FORMA_ICON_SVG[forma]?.viewBox || "0 0 512 512"} width="18" height="18" fill="currentColor" aria-hidden="true">
                      <path d={FORMA_ICON_SVG[forma]?.path || "M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM371.8 211.8C382.7 200.9 382.7 183.1 371.8 172.2C360.9 161.3 343.1 161.3 332.2 172.2L224 280.4L179.8 236.2C168.9 225.3 151.1 225.3 140.2 236.2C129.3 247.1 129.3 264.9 140.2 275.8L204.2 339.8C215.1 350.7 232.9 350.7 243.8 339.8L371.8 211.8z"} />
                    </svg>
                  }
                  value={statusCounts?.concluidosByForma?.[forma] ?? 0}
                  label={forma}
                />
              ))}
              <SubStatCard
                active={concluidosSubFilter === "todos"}
                onClick={() => { setConcluidosSubFilter("todos"); setPage(1); }}
                iconBg="#16A34A26"
                iconFg="#16A34A"
                iconSvg={
                  <svg viewBox="0 0 512 512" width="16" height="16" fill="currentColor" aria-hidden="true">
                    {ICONS.layers}
                  </svg>
                }
                value={statusCounts?.concluidos ?? 0}
                label="Todos"
              />
            </div>
          </div>

          <div className="viewWrap">
            <div className="viewWrap__header">
              <div className="viewWrap__title">
                <h2 className="sectionTitle">Expedientes ({total})</h2>
                <div className="sectionMeta">{rows.length} mostrados</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <Button
                  ghost
                  size="large"
                  className={`laboral-btn-create laboral-btn-pendiente-info${pendienteInfoFilter ? " active" : ""}`}
                  onClick={() => {
                    setPendienteInfoFilter((p) => !p);
                    setStatusFilter("todos");
                    setAudienciaSubFilter("todos");
                    setTerminoSubFilter("todos");
                    setConcluidosSubFilter("todos");
                    setPage(1);
                  }}
                >
                  <svg className="me-1" viewBox="0 0 384 512" width="13" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
                    {ICONS.file}
                  </svg>
                  Pendientes de Información ({statusCounts?.pendientesInformacion ?? 0})
                </Button>
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    className: "laboral-export-menu",
                    items: [
                      {
                        key: "export",
                        label: (
                          <ExportMenuRow
                            viewBox="0 0 384 512"
                            path="M88 304H80V256H88C101.3 256 112 266.7 112 280C112 293.3 101.3 304 88 304zM192 256H200C208.8 256 216 263.2 216 272V336C216 344.8 208.8 352 200 352H192V256zM224 0V128C224 145.7 238.3 160 256 160H384V448C384 483.3 355.3 512 320 512H64C28.65 512 0 483.3 0 448V64C0 28.65 28.65 0 64 0H224zM64 224C55.16 224 48 231.2 48 240V368C48 376.8 55.16 384 64 384C72.84 384 80 376.8 80 368V336H88C118.9 336 144 310.9 144 280C144 249.1 118.9 224 88 224H64zM160 368C160 376.8 167.2 384 176 384H200C226.5 384 248 362.5 248 336V272C248 245.5 226.5 224 200 224H176C167.2 224 160 231.2 160 240V368zM288 224C279.2 224 272 231.2 272 240V368C272 376.8 279.2 384 288 384C296.8 384 304 376.8 304 368V320H336C344.8 320 352 312.8 352 304C352 295.2 344.8 288 336 288H304V256H336C344.8 256 352 248.8 352 240C352 231.2 344.8 224 336 224H288zM256 0L384 128H256V0z"
                            tone="primary"
                            title="Exportar PDF"
                            subtitle="Genera un reporte de los expedientes filtrados"
                          />
                        ),
                        disabled: !selectedAutoridadId || !canConsultar,
                        onClick: handleExportNow,
                      },
                      ...(canInsertar ? [
                      { type: "divider" },
                      {
                        key: "importExcel",
                        label: (
                          <ExportMenuRow
                            viewBox="0 0 384 512"
                            path="M224 128L224 0H48C21.49 0 0 21.49 0 48v416C0 490.5 21.49 512 48 512h288c26.51 0 48-21.49 48-48V160h-127.1C238.3 160 224 145.7 224 128zM272.1 264.4L224 344l48.99 79.61C279.6 434.3 271.9 448 259.4 448h-26.43c-5.557 0-10.71-2.883-13.63-7.617L192 396l-27.31 44.38C161.8 445.1 156.6 448 151.1 448H124.6c-12.52 0-20.19-13.73-13.63-24.39L160 344L111 264.4C104.4 253.7 112.1 240 124.6 240h26.43c5.557 0 10.71 2.883 13.63 7.613L192 292l27.31-44.39C222.2 242.9 227.4 240 232.9 240h26.43C271.9 240 279.6 253.7 272.1 264.4zM256 0v128h128L256 0z"
                            tone="green"
                            title="Importar Excel"
                            subtitle="Carga masiva desde una plantilla"
                          />
                        ),
                        onClick: () => setImportModalOpen(true),
                      },
                      ] : []),
                    ],
                  }}
                >
                  <Button size="large" className="laboral-btn-create">
                    <svg className="me-1" viewBox="0 0 256 512" width="7" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
                      <path d="M249.6 392.3l-104 112c-9.094 9.781-26.09 9.781-35.19 0l-103.1-112c-6.484-6.984-8.219-17.17-4.406-25.92S14.45 352 24 352H80V160H24C14.45 160 5.812 154.3 1.999 145.6C-1.813 136.8-.0781 126.7 6.406 119.7l104-112c9.094-9.781 26.09-9.781 35.19 0l104 112c6.484 6.984 8.219 17.17 4.406 25.92C250.2 154.3 241.5 160 232 160H176v192h56c9.547 0 18.19 5.656 22 14.41S256.1 385.3 249.6 392.3z" />
                    </svg>
                    Exportar / Importar
                  </Button>
                </Dropdown>
                <ImportarExcelModal
                  open={importModalOpen}
                  onClose={() => setImportModalOpen(false)}
                  onSuccess={() => dispatch(actionTribunalCards(filtrosBusqueda))}
                  context={{
                    estado:            selectedEstadoId !== ALL ? selectedEstadoId : undefined,
                    ciudad:            selectedCiudadId !== ALL ? selectedCiudadId : undefined,
                    autoridad:         selectedAutoridadId !== ALL ? selectedAutoridadId : undefined,
                    tipo_conciliacion: tipoConfig.key,
                  }}
                />
                <Select
                  className="laboral-control laboral-sort-control"
                  size="large"
                  style={{ width: 240 }}
                  popupMatchSelectWidth={false}
                  value={sortOption}
                  onChange={(value) => {
                    setSortOption(value || "default");
                    setPage(1);
                  }}
                  options={[
                    { value: "default", label: "Ordenar por" },
                    { value: "az", label: "Nombre (A-Z)" },
                    { value: "za", label: "Nombre (Z-A)" },
                    { value: "audiencia_asc", label: "Fecha de audiencia · más antigua" },
                    { value: "audiencia_desc", label: "Fecha de audiencia · más reciente" },
                    { value: "subido_asc", label: "Fecha de subido · más antigua" },
                    { value: "subido_desc", label: "Fecha de subido · más reciente" },
                  ]}
                />
                <Segmented
                  className="laboral-segmented"
                  value={view}
                  onChange={setView}
                  options={[
                    {
                      value: "cards",
                      label: (
                        <span className="laboral-segmented-option">
                          <svg viewBox="0 0 320 512" width="8" height="13" fill="currentColor" aria-hidden="true">
                            <path d="M88 352C110.1 352 128 369.9 128 392V440C128 462.1 110.1 480 88 480H40C17.91 480 0 462.1 0 440V392C0 369.9 17.91 352 40 352H88zM280 352C302.1 352 320 369.9 320 392V440C320 462.1 302.1 480 280 480H232C209.9 480 192 462.1 192 440V392C192 369.9 209.9 352 232 352H280zM40 320C17.91 320 0 302.1 0 280V232C0 209.9 17.91 192 40 192H88C110.1 192 128 209.9 128 232V280C128 302.1 110.1 320 88 320H40zM280 192C302.1 192 320 209.9 320 232V280C320 302.1 302.1 320 280 320H232C209.9 320 192 302.1 192 280V232C192 209.9 209.9 192 232 192H280zM40 160C17.91 160 0 142.1 0 120V72C0 49.91 17.91 32 40 32H88C110.1 32 128 49.91 128 72V120C128 142.1 110.1 160 88 160H40zM280 32C302.1 32 320 49.91 320 72V120C320 142.1 302.1 160 280 160H232C209.9 160 192 142.1 192 120V72C192 49.91 209.9 32 232 32H280z" />
                          </svg>
                          Tarjetas
                        </span>
                      ),
                    },
                    {
                      value: "table",
                      label: (
                        <span className="laboral-segmented-option">
                          <svg viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                            <path d="M448 32C483.3 32 512 60.65 512 96V416C512 451.3 483.3 480 448 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H448zM224 256V160H64V256H224zM64 320V416H224V320H64zM288 416H448V320H288V416zM448 256V160H288V256H448z" />
                          </svg>
                          Tabla
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
            </div>

            {view === "table" ? (
              <TableView
                pageRows={rows}
                total={total}
                totalPages={totalPages}
                page={page}
                setPage={setPage}
                navigate={navigate}
                saveFiltersToSession={saveFiltersToSession}
                location={location}
                canConsultar={canConsultar}
              />
            ) : (
              <CardsView
                rows={rows}
                total={total}
                totalPages={totalPages}
                page={page}
                setPage={setPage}
                navigate={navigate}
                saveFiltersToSession={saveFiltersToSession}
                location={location}
                canConsultar={canConsultar}
              />
            )}
          </div>
        </div>
      </main>
    </div>
    </HappyProvider>
    </ConfigProvider>
  );
}
